import { Component } from '@theme/component';

/**
 * Category carousel: the card sitting at the centre of the track is the
 * highlighted one — it drops the greyscale and scales up.
 *
 * The centre is found with an IntersectionObserver whose root is the track,
 * shrunk by `rootMargin` to a thin band down the middle. Cards overlapping
 * that band are centre candidates, so there is no scroll handler and no
 * measuring on every frame. The band's width doesn't relate to the card gap,
 * so mid-scroll it may hold two candidates (band wider than the gap) or none
 * (band inside the gap): ties break to the card nearest the track's centre,
 * and an empty band keeps the previous centre until a new candidate arrives —
 * exactly one card is highlighted at all times.
 *
 * The loop: the real set is cloned once before itself and once after itself,
 * so the track is full of cards from the first paint (no blank lead-in
 * before the first real card) and the visitor can keep sliding either way
 * without hitting an end. Clones are `inert` + `aria-hidden`: Tab and
 * assistive tech only ever land on the real set. Once a scroll settles with
 * a clone centred, the track jumps — instantly, no animation — by exactly
 * one set's width, landing on the equivalent real card. The jump is
 * invisible because clone and real cards are pixel-identical.
 *
 * @extends {Component<{ trilho: HTMLElement }>}
 */
class CarrosselCategorias extends Component {
  requiredRefs = ['trilho'];

  /** @type {IntersectionObserver | undefined} */
  #observador;

  /** Cards currently overlapping the centre band. @type {Set<Element>} */
  #candidatos = new Set();

  /** Width (cards + gaps) of one full set, for the loop jump. @type {number} */
  #larguraConjunto = 0;

  /** True while a corrective jump's own `scrollend` is still pending. */
  #corrigindo = false;

  connectedCallback() {
    super.connectedCallback();

    this.#prepararLoop();

    // Without IntersectionObserver every card stays in colour: losing the
    // highlight is fine, losing the pictures is not.
    if (!('IntersectionObserver' in window)) {
      this.setAttribute('data-sem-destaque', '');
      return;
    }

    this.#observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (entrada.isIntersecting) {
            this.#candidatos.add(entrada.target);
          } else {
            this.#candidatos.delete(entrada.target);
          }
        }
        this.#destacar();
      },
      { root: this.refs.trilho, rootMargin: '0px -49% 0px -49%', threshold: 0 }
    );

    for (const card of this.#cards) this.#observador.observe(card);

    // `scrollend` fires once per gesture (arrow click, drag, momentum) —
    // exactly when to check whether we've settled on a clone.
    if ('onscrollend' in window) {
      this.refs.trilho.addEventListener('scrollend', () => this.#corrigirLoop());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observador?.disconnect();
    this.#candidatos.clear();
  }

  /**
   * Clones the real set to both sides of itself and scrolls the first real
   * card to the centre, so the initial paint is already full — no padding
   * gap waiting to be scrolled into.
   */
  #prepararLoop() {
    const trilho = this.refs.trilho;
    const reais = this.#cards;
    if (reais.length === 0) return;

    const clonar = (grupo) =>
      reais.map((card) => {
        const clone = /** @type {HTMLElement} */ (card.cloneNode(true));
        clone.dataset.loopGrupo = grupo;
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        return clone;
      });

    trilho.append(...clonar('depois'));
    trilho.prepend(...clonar('antes'));

    const espaco = Number.parseFloat(getComputedStyle(trilho).columnGap) || 0;
    this.#larguraConjunto = reais.reduce((soma, card) => soma + card.offsetWidth + espaco, 0);

    this.#centralizar(reais[0]);
  }

  /**
   * Centres a card in the track by writing the track's own scroll offset.
   *
   * `scrollIntoView` is the one-liner for this, but it walks up **every**
   * scrollable ancestor, and `block: 'nearest'` only means "scroll the
   * minimum" — with the carousel below the fold it still dragged the page
   * itself down to the section on first paint, so the visitor landed in the
   * middle of the home instead of at the top (ULTI-11). Moving `scrollLeft`
   * touches the track and nothing else.
   *
   * @param {HTMLElement | undefined} card
   */
  #centralizar(card) {
    if (!card) return;

    const trilho = this.refs.trilho;
    const caixaTrilho = trilho.getBoundingClientRect();
    const caixaCard = card.getBoundingClientRect();
    const desvio = caixaCard.left + caixaCard.width / 2 - (caixaTrilho.left + caixaTrilho.width / 2);

    trilho.scrollLeft += desvio;
  }

  /**
   * After a scroll settles on a clone, jump silently to its real twin.
   *
   * Finds the centred card itself, by geometry, instead of reading
   * `.is-centro` — that class is set by the IntersectionObserver callback,
   * which runs asynchronously and may still hold the pre-scroll card when
   * `scrollend` fires. Reading stale state here previously produced a
   * second, wrong correction on top of the first.
   *
   * The corrective jump is itself a scroll, so it queues its own
   * `scrollend`. `#corrigindo` swallows exactly that one, so it isn't
   * mistaken for a settle that needs correcting again.
   */
  #corrigirLoop() {
    if (this.#corrigindo) {
      this.#corrigindo = false;
      return;
    }
    if (!this.#larguraConjunto) return;

    const trilho = this.refs.trilho;
    const caixa = trilho.getBoundingClientRect();
    const centroAlvo = caixa.left + caixa.width / 2;

    let eleito = null;
    let menorDistancia = Infinity;
    for (const card of this.#cards) {
      const cardBox = card.getBoundingClientRect();
      const distancia = Math.abs(cardBox.left + cardBox.width / 2 - centroAlvo);
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        eleito = card;
      }
    }

    const grupo = eleito?.dataset.loopGrupo;
    if (!grupo) return;

    this.#corrigindo = true;
    trilho.scrollLeft += grupo === 'antes' ? this.#larguraConjunto : -this.#larguraConjunto;
    // Fallback in case this engine never fires `scrollend` for a plain
    // `scrollLeft` write: don't leave the guard stuck true forever. If the
    // real `scrollend` arrives after this, it just re-checks geometry and
    // finds a real card — a harmless no-op.
    setTimeout(() => {
      this.#corrigindo = false;
    }, 150);
  }

  /** @returns {HTMLElement[]} */
  get #cards() {
    return Array.from(this.refs.trilho.querySelectorAll('.carrossel-cat__card'));
  }

  /** Moves `.is-centro` to the candidate nearest the centre of the track. */
  #destacar() {
    if (this.#candidatos.size === 0) return;

    const trilho = this.refs.trilho.getBoundingClientRect();
    const centro = trilho.left + trilho.width / 2;

    let eleito = null;
    let menorDistancia = Infinity;

    for (const card of this.#candidatos) {
      const caixa = card.getBoundingClientRect();
      const distancia = Math.abs(caixa.left + caixa.width / 2 - centro);
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        eleito = card;
      }
    }

    for (const card of this.#cards) {
      card.classList.toggle('is-centro', card === eleito);
    }
  }

  anterior() {
    this.#deslizar(-1);
  }

  proximo() {
    this.#deslizar(1);
  }

  /** @param {number} direcao */
  #deslizar(direcao) {
    const trilho = this.refs.trilho;
    const [card] = this.#cards;
    if (!card) return;

    const espaco = Number.parseFloat(getComputedStyle(trilho).columnGap) || 0;
    // `offsetWidth` is the layout width: unlike getBoundingClientRect, it
    // ignores the `scale()` of a highlighted card, so the step is exact.
    const passo = card.offsetWidth + espaco;

    trilho.scrollBy({ left: passo * direcao, behavior: 'smooth' });
  }
}

// The `-component` suffix is load-bearing: the theme's delegated event
// handling (component.js) only routes `on:click` & co. — and only closes the
// custom-element upgrade race — for tags ending in `-component`.
if (!customElements.get('carrossel-categorias-component')) {
  customElements.define('carrossel-categorias-component', CarrosselCategorias);
}
